import {
  KoButton,
  KoCharts,
  KoLoader,
  KoSelect,
  KoSkeleton,
  KoTable,
  KoTitle,
  KoTag,
  KoToast,
  KoEmpty
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { MonitoringAPI } from '../services/monitoring.service';
import { COLUMNS_RELEASE_MONITORING, MONITORING_WINDOW_CONFIG } from '../constants';
import { metricsComparisonChart } from '../charts/metricsComparisonChart';
import MonitoringSubNav from './MonitoringSubNav';
import styles from './monitoring.module.scss';

const MOCK_RELEASE_LIST = [
  { label: 'v4.2.0 — Platform Upgrade', value: 'REL-001' },
  { label: 'v4.1.3 — Auth Redesign', value: 'REL-002' },
  { label: 'v4.1.4 — Hotfix: Payment Gateway', value: 'REL-005' }
];

const MOCK_WINDOW = {
  status: 'active',
  startTime: '2026-02-24T06:00:00Z',
  endTime: '2026-02-24T12:00:00Z',
  duration: '6h'
};

const MOCK_COMPARISON = [
  { metric: 'Error Rate', releaseName: 'v4.2.0', service: 'api-gateway', preValue: 0.12, postValue: 0.18, deviation: 50.0, status: 'active' },
  { metric: 'P95 Latency', releaseName: 'v4.2.0', service: 'api-gateway', preValue: 45, postValue: 52, deviation: 15.6, status: 'passed' },
  { metric: 'Error Rate', releaseName: 'v4.2.0', service: 'payment-processor', preValue: 0.05, postValue: 2.45, deviation: 4800.0, status: 'failed' },
  { metric: 'P95 Latency', releaseName: 'v4.2.0', service: 'payment-processor', preValue: 120, postValue: 320, deviation: 166.7, status: 'failed' },
  { metric: 'CPU Usage', releaseName: 'v4.2.0', service: 'search-engine', preValue: 45.2, postValue: 95.2, deviation: 110.6, status: 'failed' },
  { metric: 'Memory Usage', releaseName: 'v4.2.0', service: 'search-engine', preValue: 62.0, postValue: 92.4, deviation: 49.0, status: 'active' },
  { metric: 'Error Rate', releaseName: 'v4.2.0', service: 'auth-service', preValue: 0.08, postValue: 0.10, deviation: 25.0, status: 'passed' },
  { metric: 'RPS', releaseName: 'v4.2.0', service: 'notification-hub', preValue: 2100, postValue: 2050, deviation: -2.4, status: 'passed' }
];

function ReleaseMonitoring() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [releaseOptions, setReleaseOptions] = useState([]);
  const [selectedRelease, setSelectedRelease] = useState(null);
  const [monitoringWindow, setMonitoringWindow] = useState(null);
  const [comparison, setComparison] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [chartsLoading, setChartsLoading] = useState(false);

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/monitoring`
    : '/monitoring';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'Monitoring', path: basePath },
      { title: 'Release Monitoring' }
    ]));
    fetchReleases();
  }, [workspaceId]);

  useEffect(() => {
    if (selectedRelease) {
      fetchReleaseData();
    }
  }, [selectedRelease]);

  const fetchReleases = () => {
    MonitoringAPI.getReleaseList({ limit: 50 }).then(
      (data) => {
        const list = (data?.data || data || []).map(r => ({
          label: `${r.version} — ${r.name}`,
          value: r._id || r.releaseId
        }));
        setReleaseOptions(list);
        if (list.length > 0 && !selectedRelease) {
          setSelectedRelease(list[0].value);
        }
      },
      () => {
        setReleaseOptions(MOCK_RELEASE_LIST);
        if (!selectedRelease) {
          setSelectedRelease(MOCK_RELEASE_LIST[0].value);
        }
      }
    );
  };

  const fetchReleaseData = () => {
    setLoading(true);
    setChartsLoading(true);

    Promise.all([
      MonitoringAPI.getMonitoringWindow(selectedRelease),
      MonitoringAPI.getReleaseComparison(selectedRelease)
    ]).then(
      ([windowData, compData]) => {
        setMonitoringWindow(windowData?.window || windowData || null);
        setComparison(compData?.data || compData || []);
        setLoading(false);
        setChartsLoading(false);
      },
      () => {
        setMonitoringWindow(MOCK_WINDOW);
        setComparison(MOCK_COMPARISON);
        setLoading(false);
        setChartsLoading(false);
      }
    );
  };

  const handleMarkPassed = () => {
    setLoading(true);
    MonitoringAPI.markWindowPassed(selectedRelease).then(
      () => { fetchReleaseData(); KoToast.success('Monitoring window marked as passed.'); },
      () => { setLoading(false); KoToast.error('Failed to mark window.'); }
    );
  };

  const handleExtendWindow = () => {
    setLoading(true);
    MonitoringAPI.extendWindow(selectedRelease, { duration: 30 }).then(
      () => { fetchReleaseData(); KoToast.success('Monitoring window extended by 30 minutes.'); },
      () => { setLoading(false); KoToast.error('Failed to extend window.'); }
    );
  };

  const handleTriggerRollback = () => {
    setLoading(true);
    MonitoringAPI.triggerRollback(selectedRelease, { reason: 'Manual rollback from monitoring' }).then(
      () => { fetchReleaseData(); KoToast.success('Rollback triggered.'); },
      () => { setLoading(false); KoToast.error('Rollback failed.'); }
    );
  };

  const windowCfg = monitoringWindow?.status
    ? MONITORING_WINDOW_CONFIG[monitoringWindow.status] || {}
    : {};

  const elapsedPercent = monitoringWindow?.startTime && monitoringWindow?.endTime
    ? Math.min(100, Math.round(
      ((Date.now() - new Date(monitoringWindow.startTime).getTime()) /
        (new Date(monitoringWindow.endTime).getTime() - new Date(monitoringWindow.startTime).getTime())) * 100
    ))
    : 0;

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <MonitoringSubNav activeKey='release-monitoring' />

      <KoToolBar
        title='Release Monitoring'
        search={{ visible: false }}
        filter={{ visible: false, options: [] }}
        extraComponentBefore={
          <KoSelect
            className={styles.releaseSelector}
            value={selectedRelease}
            onChange={setSelectedRelease}
            placeholder='Select a release'
            options={releaseOptions}
            style={{ minWidth: 260 }}
          />
        }
        buttonList={[
          {
            label: 'Refresh',
            icon: <KoIcon color='#565555' name='ReloadOutlined' width={16} height={16} />,
            onClick: fetchReleaseData
          }
        ]}
      />

      {!selectedRelease && !isLoading && (
        <KoEmpty description='Select a release to view monitoring data' />
      )}

      {selectedRelease && monitoringWindow && (
        <>
          {/* Monitoring Window Card */}
          <div className={styles.monitoringWindowCard}>
            <div className={styles.windowHeader}>
              <div className={styles.windowHeaderLeft}>
                <KoTitle level={5} text='Monitoring Window' />
                <KoTag color={windowCfg.color}>{windowCfg.label || monitoringWindow.status}</KoTag>
              </div>
            </div>

            <div className={styles.windowMeta}>
              <div className={styles.windowMetaItem}>
                <span className={styles.windowMetaLabel}>Start Time</span>
                <span className={styles.windowMetaValue}>{monitoringWindow.startTime ? new Date(monitoringWindow.startTime).toLocaleString() : '—'}</span>
              </div>
              <div className={styles.windowMetaItem}>
                <span className={styles.windowMetaLabel}>End Time</span>
                <span className={styles.windowMetaValue}>{monitoringWindow.endTime ? new Date(monitoringWindow.endTime).toLocaleString() : '—'}</span>
              </div>
              <div className={styles.windowMetaItem}>
                <span className={styles.windowMetaLabel}>Duration</span>
                <span className={styles.windowMetaValue}>{monitoringWindow.duration || '—'}</span>
              </div>
              <div className={styles.windowMetaItem}>
                <span className={styles.windowMetaLabel}>Elapsed</span>
                <span className={styles.windowMetaValue}>{elapsedPercent}%</span>
              </div>
            </div>

            {monitoringWindow.status === 'active' && (
              <div className={styles.windowProgress}>
                <div className={styles.windowProgressFill} style={{ width: `${elapsedPercent}%` }} />
              </div>
            )}

            <div className={styles.windowActions}>
              <KoButton label='Extend Window' onClick={handleExtendWindow} icon={<KoIcon name='ClockCircleOutlined' width={14} height={14} />} />
              <KoButton label='Mark Passed' type='primary' onClick={handleMarkPassed} icon={<KoIcon name='CheckCircleOutlined' width={14} height={14} color='#FFF' />} />
              <KoButton label='Trigger Rollback' danger onClick={handleTriggerRollback} icon={<KoIcon name='RollbackOutlined' width={14} height={14} />} />
            </div>
          </div>

          {/* Comparison Chart */}
          <div className={styles.comparisonSection}>
            <KoTitle level={5} text='Pre-Deploy vs Post-Deploy Comparison' style={{ marginBottom: 16 }} />
            <div className={styles.chartCard}>
              {chartsLoading && <KoSkeleton active paragraph={{ rows: 8 }} />}
              {!chartsLoading && comparison.length > 0 && (
                <KoCharts option={metricsComparisonChart(comparison)} />
              )}
              {!chartsLoading && comparison.length === 0 && (
                <KoEmpty description='No comparison data available' />
              )}
            </div>
          </div>

          {/* Deviation Table */}
          {comparison.length > 0 && (
            <div style={{ padding: '0 1.6rem 1.6rem' }}>
              <KoTitle level={5} text='Metric Deviations' style={{ marginBottom: 12 }} />
              <KoTable
                maxHeight={360}
                dataSource={comparison}
                columns={COLUMNS_RELEASE_MONITORING}
                rowKey={(r) => r.metric || r._id}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ReleaseMonitoring;
